/**
 * AssetFilterRail - hybrid top-filter controls for the Image Assets Library.
 *
 * Controlled component: it reads the current filter params from `value` and
 * reports changes via `onChange` with a partial patch. Filter changes (search,
 * category, state, label_status, include_archived) reset `offset` to 0 so
 * pagination starts from the first page of the new result set.
 *
 * Search and primary facets remain visible. Advanced controls disclose on
 * demand while active-filter chips preserve the visibility of hidden state.
 */

import { useEffect, useState } from "react"
import { ChevronDown, Search } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { COPY } from "@/lib/copyTokens"
import type { ClientImageAssetListParams } from "@/services/clientImageAssetsService"

export interface AssetFilterValue extends ClientImageAssetListParams {}

export interface AssetFilterRailProps {
  value: AssetFilterValue
  onChange: (patch: Partial<AssetFilterValue>) => void
  className?: string
}

// Sentinel for "no filter" — Radix Select items cannot use empty-string values.
const ALL = "__all__"

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: "product", label: COPY.mediaAssets.categories.product },
  { value: "project", label: COPY.mediaAssets.categories.project },
  { value: "location", label: COPY.mediaAssets.categories.location },
  { value: "team", label: COPY.mediaAssets.categories.team },
  { value: "gallery", label: COPY.mediaAssets.categories.gallery },
  { value: "logo", label: COPY.mediaAssets.categories.logo },
  { value: "service", label: COPY.mediaAssets.categories.service },
  { value: "other", label: COPY.mediaAssets.categories.other },
]

const STATE_OPTIONS: { value: string; label: string }[] = [
  { value: "web-ready", label: COPY.mediaAssets.states.webReady },
  { value: "source-only", label: COPY.mediaAssets.states.sourceOnly },
  { value: "needs-review", label: COPY.mediaAssets.states.needsReview },
  { value: "do-not-use", label: COPY.mediaAssets.states.doNotUse },
  { value: "archived", label: COPY.mediaAssets.states.archived },
]

const LABEL_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "labeled", label: COPY.mediaAssets.labelStatuses.labeled },
  { value: "needs_review", label: COPY.mediaAssets.labelStatuses.needsReview },
  { value: "approved", label: COPY.mediaAssets.labelStatuses.approved },
  { value: "do_not_use", label: COPY.mediaAssets.labelStatuses.doNotUse },
]

function FilterSelect({
  label,
  placeholder,
  value,
  options,
  onValueChange,
}: {
  label: string
  placeholder: string
  value: string | undefined
  options: { value: string; label: string }[]
  onValueChange: (next: string | undefined) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <Select
        value={value ?? ALL}
        onValueChange={(next) => onValueChange(next === ALL ? undefined : next)}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{placeholder}</SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export function AssetFilterRail({ value, onChange, className }: AssetFilterRailProps) {
  // Local search state for debounced commit to the parent.
  const [search, setSearch] = useState(value.q ?? "")
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false)

  // Keep local input in sync if the parent resets filters externally.
  useEffect(() => {
    setSearch(value.q ?? "")
  }, [value.q])

  // Debounce the search → parent (resets offset to 0).
  useEffect(() => {
    const next = search.trim() || undefined
    if (next === (value.q ?? undefined)) return
    const handle = setTimeout(() => {
      onChange({ q: next, offset: 0 })
    }, 350)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const activeFilters = [
    value.q ? { key: "q", label: value.q } : null,
    value.category
      ? { key: "category", label: CATEGORY_OPTIONS.find((option) => option.value === value.category)?.label ?? value.category }
      : null,
    value.state
      ? { key: "state", label: STATE_OPTIONS.find((option) => option.value === value.state)?.label ?? value.state }
      : null,
    value.label_status
      ? { key: "label_status", label: LABEL_STATUS_OPTIONS.find((option) => option.value === value.label_status)?.label ?? value.label_status }
      : null,
    value.include_archived ? { key: "include_archived", label: COPY.mediaAssets.showArchived } : null,
  ].filter((filter): filter is { key: keyof AssetFilterValue; label: string } => Boolean(filter))

  const clearFilter = (key: keyof AssetFilterValue) => {
    if (key === "q") setSearch("")
    onChange({ [key]: key === "include_archived" ? false : undefined, offset: 0 })
  }

  return (
    <section aria-label={COPY.mediaAssets.filterRegion} className={cn("rounded-2xl border border-border bg-card p-4", className)}>
      <div className="grid gap-3 md:grid-cols-[minmax(15rem,1.5fr)_repeat(2,minmax(10rem,1fr))_auto] md:items-end">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">{COPY.mediaAssets.search}</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={COPY.mediaAssets.searchPlaceholder}
              className="pl-8"
            />
          </div>
        </div>

        <FilterSelect
          label={COPY.mediaAssets.category}
          placeholder={COPY.mediaAssets.allCategories}
          value={value.category}
          options={CATEGORY_OPTIONS}
          onValueChange={(next) => onChange({ category: next, offset: 0 })}
        />

        <FilterSelect
          label={COPY.mediaAssets.state}
          placeholder={COPY.mediaAssets.allStates}
          value={value.state}
          options={STATE_OPTIONS}
          onValueChange={(next) => onChange({ state: next, offset: 0 })}
        />

        <Button
          type="button"
          variant="outline"
          onClick={() => setMoreFiltersOpen((open) => !open)}
          aria-expanded={moreFiltersOpen}
          className="justify-between"
        >
          {COPY.mediaAssets.moreFilters}
          <ChevronDown className={`h-4 w-4 transition-transform${moreFiltersOpen ? " rotate-180" : ""}`} />
        </Button>
      </div>

      {moreFiltersOpen ? (
        <div className="mt-3 grid gap-3 border-t border-border pt-3 md:grid-cols-2 md:items-end">
          <FilterSelect
            label={COPY.mediaAssets.labelStatus}
            placeholder={COPY.mediaAssets.allLabelStatuses}
            value={value.label_status}
            options={LABEL_STATUS_OPTIONS}
            onValueChange={(next) => onChange({ label_status: next, offset: 0 })}
          />
          <div className="flex items-center justify-between rounded-lg border border-border bg-warm-bg px-3 py-2">
            <div className="space-y-0.5">
              <Label htmlFor="asset-show-archived" className="text-sm font-medium text-foreground">
                {COPY.mediaAssets.showArchived}
              </Label>
              <p className="text-xs text-muted-foreground">{COPY.mediaAssets.showArchivedHelp}</p>
            </div>
            <Switch
              id="asset-show-archived"
              checked={Boolean(value.include_archived)}
              onCheckedChange={(checked) => onChange({ include_archived: checked, offset: 0 })}
            />
          </div>
        </div>
      ) : null}

      {activeFilters.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3" data-testid="active-asset-filters">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {COPY.mediaAssets.activeFilters}
          </span>
          {activeFilters.map((filter) => (
            <Button
              key={filter.key}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => clearFilter(filter.key)}
              aria-label={COPY.mediaAssets.removeFilter(filter.label)}
              className="h-6 rounded-full px-2 text-[11px] text-ink-2"
            >
              {filter.label}
            </Button>
          ))}
        </div>
      ) : null}
    </section>
  )
}

export default AssetFilterRail
